import { useState, forwardRef, useRef } from 'react';
import { Transition } from 'react-transition-group';
import { Alert as MuiAlert, AlertTitle as MuiAlertTitle, IconButton } from '@mui/material';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '../../Icons';
import alertStyleBuilder from './alertStyleBuilder';
import alertTitleDefaultStyle from './alertTitleDefaultStyle';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { Box } from '../../Box';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const iconMapping = {
  success: /*#__PURE__*/_jsx(CheckCircleIcon, {
    fontSize: '20px'
  }),
  warning: /*#__PURE__*/_jsx(ExclamationTriangleIcon, {
    fontSize: '20px'
  }),
  info: /*#__PURE__*/_jsx(InformationCircleIcon, {
    fontSize: '20px'
  }),
  error: /*#__PURE__*/_jsx(ExclamationCircleIcon, {
    fontSize: '20px'
  })
};
const defaultStyle = {
  transition: 'opacity 500ms ease-in-out',
  opacity: 0
};
const transitionStyles = {
  entering: {
    opacity: 1
  },
  entered: {
    opacity: 1
  },
  exiting: {
    opacity: 0
  },
  exited: {
    opacity: 0
  }
};

/** Banners showing status messages  */
export const Alert = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    disableSuccessFade,
    displayCloseButton = false,
    children,
    id,
    onCloseClick,
    role = 'alert',
    sx,
    titleSx,
    type,
    title,
    className,
    ...rest
  } = _ref;
  const [showAlert, setShowAlert] = useState(true);
  const nodeRef = useRef(null);
  const theme = useInterstateTheme();
  const alertStyle = mergeSxStyle(alertStyleBuilder(type, theme, displayCloseButton), sx);
  const alertTitleStyle = mergeSxStyle(alertTitleDefaultStyle(theme), titleSx);
  const handleCloseClick = () => {
    setShowAlert(false);
    if (onCloseClick) onCloseClick();
  };
  if (type === 'success' && !disableSuccessFade) {
    setTimeout(() => setShowAlert(false), 5000);
  }
  const _type = type === 'caution' ? 'warning' : type;
  return /*#__PURE__*/_jsx(Transition, {
    nodeRef: nodeRef,
    in: showAlert,
    timeout: 500,
    unmountOnExit: true,
    onExited: handleCloseClick,
    children: state => /*#__PURE__*/_jsx(Box, {
      ref: nodeRef,
      sx: {
        ...defaultStyle,
        ...transitionStyles[state]
      },
      children: /*#__PURE__*/_jsxs(MuiAlert, {
        action: /*#__PURE__*/_jsx(_Fragment, {
          children: displayCloseButton && type === 'info' && /*#__PURE__*/_jsx(IconButton, {
            className: "ids-alert-close-btn",
            "aria-label": "Close",
            size: "small",
            disableRipple: true,
            id: id ? `ids-close-button-${id}` : 'ids-close-button',
            onClick: handleCloseClick,
            "data-testid": rest['data-testid'] ? `${rest['data-testid']}-ids-close-button` : `${type}-ids-close-button`,
            sx: {
              padding: '0px'
            },
            children: /*#__PURE__*/_jsx(XMarkIcon, {})
          })
        }),
        id: id,
        role: role,
        ref: ref,
        severity: _type,
        variant: "outlined",
        sx: alertStyle,
        iconMapping: iconMapping,
        "data-testid": rest['data-testid'],
        className: className,
        children: [title && /*#__PURE__*/_jsx(MuiAlertTitle, {
          sx: alertTitleStyle,
          children: title
        }), children]
      })
    })
  });
});
Alert.displayName = 'Alert';